import React from 'react';
import { Box, Container, Typography, Grid, Card, CardContent, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import CloudQueueOutlinedIcon from '@mui/icons-material/CloudQueueOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import HeadsetMicOutlinedIcon from '@mui/icons-material/HeadsetMicOutlined';

const MotionBox = motion(Box);
const MotionCard = motion(Card);

const services = [
  {
    icon: CloudQueueOutlinedIcon,
    title: 'Cloud-Based Financial Solutions',
    description: 'Our cloud-based financial solutions provide easy access to your financial data from anywhere, at any time.'
  },
  {
    icon: TrendingUpOutlinedIcon,
    title: 'Industry-Leading Financial Reporting',
    description: 'Our financial reporting services are designed to provide you with industry-leading insights and analytics. Let us help you make informed financial decisions for your business.'
  },
  {
    icon: BusinessCenterOutlinedIcon,
    title: 'A Range of Financial Services',
    description: 'We offer a range of financial services to help businesses manage their financial records and reporting requirements. From bookkeeping to financial planning, we have you covered.'
  },
  {
    icon: HeadsetMicOutlinedIcon,
    title: 'Dedicated Customer Support',
    description: 'At F-ai, we pride ourselves on providing dedicated customer support to all of our clients. We are here to help you every step of the way.'
  }
];

const Guidelines = () => {
  const theme = useTheme();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        background: `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 50%, ${theme.palette.background.default} 100%)`,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background decorative elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          right: '-100px',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${theme.palette.primary.main}20 0%, transparent 70%)`,
          filter: 'blur(50px)',
          pointerEvents: 'none'
        }}
      />
      
      <MotionBox
        animate={{
          rotate: 360
        }}
        transition={{
          duration: 30,
          repeat: Infinity,
          ease: "linear"
        }}
        sx={{
          position: 'absolute',
          bottom: '10%',
          left: '-80px',
          width: '250px',
          height: '250px',
          border: `2px solid ${theme.palette.secondary.main}20`,
          borderRadius: '50%',
          pointerEvents: 'none',
          display: { xs: 'none', md: 'block' }
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
          {/* Left Side - Content */}
          <Grid item xs={12} md={5}>
            <MotionBox
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <Typography
                variant="overline"
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                  letterSpacing: 2,
                  mb: 2,
                  display: 'block'
                }}
              >
                WHY CHOOSE F-AI
              </Typography>

              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '2rem', md: '2.75rem', lg: '3.25rem' },
                  lineHeight: 1.2,
                  mb: 3,
                  color: theme.palette.text.primary
                }}
              >
                Expert Financial Guidance
                <Box
                  component="span"
                  sx={{
                    display: 'block',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  for Your Business
                </Box>
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.secondary,
                  lineHeight: 1.8,
                  mb: 2
                }}
              >
                At <strong style={{ color: theme.palette.primary.main }}>f-ai</strong>, we specialize in providing finance accounting, bookkeeping, and financial reporting services to help businesses like yours thrive.
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.secondary,
                  lineHeight: 1.8,
                  mb: 2
                }}
              >
                Our AI tool simplifies the bookkeeping, accounting and financial reporting process. With Real time data visualization you get an MIS dashboard for making Informed decisions.
              </Typography>

              <MotionBox
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                sx={{
                  mt: 4,
                  p: 3,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}15)`,
                  borderLeft: `4px solid ${theme.palette.primary.main}`
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.text.primary,
                    fontWeight: 500,
                    fontStyle: 'italic'
                  }}
                >
                  Our Team is dedicated to providing you with the best financial guidance available.
                </Typography>
              </MotionBox>

              {/* Animated stats or badges */}
              <Grid container spacing={2} sx={{ mt: 4 }}>
                {['AI-Powered', 'Real-Time Data', '24/7 Support'].map((badge, index) => (
                  <Grid item key={index}>
                    <MotionBox
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: 0.4 + index * 0.1 }}
                      whileHover={{ scale: 1.05 }}
                      sx={{
                        py: 1,
                        px: 2.5,
                        borderRadius: 2,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        boxShadow: `0 4px 12px ${theme.palette.primary.main}30`
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#fff',
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}
                      >
                        {badge}
                      </Typography>
                    </MotionBox>
                  </Grid>
                ))}
              </Grid>
            </MotionBox>
          </Grid>

          {/* Right Side - Cards */}
          <Grid item xs={12} md={7}>
            <MotionBox
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-50px' }}
            >
              <Grid container spacing={3}>
                {services.map((service, index) => {
                  const Icon = service.icon;
                  return (
                    <Grid item xs={12} sm={6} key={index}>
                      <MotionCard
                        variants={cardVariants}
                        whileHover={{
                          y: -8,
                          boxShadow: `0 16px 32px ${theme.palette.primary.main}20`
                        }}
                        sx={{
                          height: '100%',
                          background: theme.palette.background.paper,
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.divider}`,
                          transition: 'all 0.3s ease',
                          position: 'relative',
                          overflow: 'hidden',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '3px',
                            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                            opacity: 0,
                            transition: 'opacity 0.3s ease'
                          },
                          '&:hover::before': {
                            opacity: 1
                          }
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <MotionBox
                            whileHover={{ rotate: [0, -10, 10, -10, 0] }}
                            transition={{ duration: 0.5 }}
                            sx={{
                              width: 56,
                              height: 56,
                              borderRadius: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: `linear-gradient(135deg, ${theme.palette.primary.main}20, ${theme.palette.secondary.main}20)`,
                              mb: 2.5
                            }}
                          >
                            <Icon sx={{ fontSize: 28, color: theme.palette.primary.main }} />
                          </MotionBox>
                          
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              mb: 1.5,
                              color: theme.palette.text.primary,
                              fontSize: '1.1rem'
                            }}
                          >
                            {service.title}
                          </Typography>
                          
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.secondary,
                              lineHeight: 1.7,
                              fontSize: '0.9rem'
                            }}
                          >
                            {service.description}
                          </Typography>
                        </CardContent>
                      </MotionCard>
                    </Grid>
                  );
                })}
              </Grid>
            </MotionBox>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Guidelines;